import { BrandMark, SCREEN_ICONS, SignOutIcon } from './icons';
import { MENU_ITEMS, pathFor, type Screen } from './screens';

/**
 * The main menu (C4a shell fix; the bar's shape corrected 2026-10-18).
 *
 * **A single-row bar across the top, at every width.** The operator's own words, after walking the
 * first shape on the live site: *"Put the menu as a menu bar on the top (just 1 row), Sign out also
 * goes to the top menu bar. Your session goes to the bottom. Use icons as well."* The left rail he
 * had approved earlier the same day — and the wrapping row it became below 900 px — is history:
 * this is the instruction the bar is built to, and `records/` says so.
 *
 * What the bar is made of, in one row that does not wrap and does not scroll:
 *
 *   - **The brand**: the mark from 381 px up and the wordmark from 641 px, and at the narrowest phones
 *     the row belongs to the destinations entirely — the stylesheet carries the measurement that
 *     decided each break.
 *   - **One item per screen**, in the registry's order, each an icon **and** its label: the label
 *     carries the meaning, and the icon is `aria-hidden` so it is never the only thing announced.
 *   - **Sign out**, at the row's end, reachable from every screen without opening anything first.
 *
 * Each item is a real `<a href>`, so the address of a screen can be copied, opened in a new tab or
 * bookmarked, and the browser's own "open in new tab" works. A plain left click is intercepted and
 * handed to the shell instead, so the screen changes without a document load: the session's own
 * state stays, and the address is written in exactly one place. Every other click — a modifier, a
 * middle click, a new tab — is left to the browser, where the `href` is the real address.
 *
 * `aria-current="page"` marks the screen you are on, which is also what the stylesheet paints.
 *
 * **The bar is one component, rendered once.** `App.tsx` renders this and nothing else renders a bar;
 * the items come from `MENU_ITEMS` in `screens.ts`, so a per-screen copy would have to duplicate the
 * registry as well. There is no per-screen menu.
 *
 * Sign out keeps its label while it is working instead of becoming *"Signing out…"*: at 360 px the
 * longer string is wider than the space the row has left, and a control that reflows the bar under
 * the finger that pressed it is worse than one that dims (`aria-busy`, painted in the stylesheet).
 */
export function Menu({
  screen,
  onSelect,
  onSignOut,
  signingOut,
}: {
  readonly screen: Screen;
  readonly onSelect: (screen: Screen) => void;
  readonly onSignOut: () => void;
  readonly signingOut: boolean;
}) {
  return (
    <header className="menubar" data-testid="menubar">
      <h1 className="brand">
        <span className="brand-mark">
          <BrandMark />
        </span>
        <span className="brand-word">Saldo</span>
      </h1>

      <nav className="menubar-nav" aria-label="Main navigation">
        {MENU_ITEMS.map((item) => {
          const current = item.screen === screen;
          const Icon = SCREEN_ICONS[item.screen];
          return (
            <a
              key={item.screen}
              className="nav-item"
              href={pathFor(item.screen)}
              data-testid={`nav-${item.screen}`}
              aria-current={current ? 'page' : undefined}
              onClick={(event) => {
                if (event.defaultPrevented || event.button !== 0) return;
                if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                event.preventDefault();
                if (!current) onSelect(item.screen);
              }}
            >
              <span className="nav-icon">
                <Icon />
              </span>
              <span className="nav-label">{item.label}</span>
            </a>
          );
        })}
      </nav>

      {/* The owner's own control, in the bar so it is one press from every screen. The private
          region it ends is the footer below the screen — the session block itself is not here. */}
      <button
        type="button"
        className="nav-item nav-signout"
        data-testid="sign-out"
        aria-busy={signingOut}
        aria-disabled={signingOut}
        onClick={onSignOut}
      >
        <span className="nav-icon">
          <SignOutIcon />
        </span>
        <span className="nav-label">Sign out</span>
      </button>
    </header>
  );
}
